/**
 * ResultSection
 *
 * Shown while the job is in-progress (polling) or completed.
 *
 * States:
 *   polling   → progress bar + status message
 *   completed → success banner + download ZIP button + "Generate another" button
 *   cancelled → cancelled banner + reset button
 *   error     → error banner (GenerateButton shows Retry)
 */

import { memo } from 'react';
import { Download, FolderOpen, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { ProgressBar } from '@/components/common/ProgressBar';
import { useTranslation } from '@/hooks/useTranslation';
import { useResultDownload } from '@/hooks/useResultDownload';
import { useSubtitles } from '../SubtitlesContext.context';

export const ResultSection = memo(function ResultSection() {
  const { t } = useTranslation();
  const { state, isGenerating, isCompleted, getDownloadUrl, handleReset } = useSubtitles();
  const { jobProgress, jobMessage, status, error, targetLanguages, outputMode, outputFilePath } =
    state;

  const isSoftEmbedded = outputMode === 'soft_embedded';
  const isHardBurn = outputMode === 'hard_burn';
  const isMp4Output = isSoftEmbedded || isHardBurn;

  const downloadUrl = getDownloadUrl();

  // Delivery goes through the shared hook: reveal-in-Finder when the artifact
  // is on this machine, native save dialog on desktop otherwise, proxy
  // download on the web. This used to be a bare `<a href download>`, which in
  // Electron hands the file to Chromium's own download manager whose Save-As
  // dialog can open behind the main window.
  const { isDownloading, handleDownload, showOpenFolder, handleOpenFolder } = useResultDownload(
    downloadUrl,
    'subtitles',
    isMp4Output ? 'mp4' : 'zip',
    { localPath: outputFilePath }
  );

  // Show nothing when form is visible and no job started yet
  if (status === 'idle' || status === 'uploading') return null;

  return (
    <div className="bg-black/30 rounded-lg border border-white/10 p-4 sm:p-6 space-y-4">
      {/* Polling / in-progress */}
      {isGenerating && (
        <>
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-purple-400 animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">
                {t('subtitles.result.generating', { defaultValue: 'Generating subtitles…' })}
              </p>
              {jobMessage && <p className="text-xs text-white/50 mt-0.5">{jobMessage}</p>}
            </div>
          </div>
          <ProgressBar percent={jobProgress} showLabel />
        </>
      )}

      {/* Completed */}
      {isCompleted && (
        <>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">
                {t('subtitles.result.completed', {
                  defaultValue: 'Subtitle files are ready!',
                })}
              </p>
              <p className="text-xs text-white/50 mt-0.5">
                {isSoftEmbedded
                  ? t('subtitles.result.completedDetailMp4', {
                      defaultValue: '{count} subtitle track(s) embedded in the MP4.',
                      count: targetLanguages.length,
                    })
                  : isHardBurn
                    ? t('subtitles.result.completedDetailHardBurn', {
                        defaultValue: 'Subtitles burned into video pixels. Language: {lang}.',
                        lang: targetLanguages[0] ?? '',
                      })
                    : t('subtitles.result.completedDetail', {
                        defaultValue: '{count} language(s) — SRT, VTT and ASS included in the ZIP.',
                        count: targetLanguages.length,
                      })}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {showOpenFolder ? (
              <button
                onClick={() => void handleOpenFolder()}
                className="min-h-[44px] flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <FolderOpen className="h-4 w-4" />
                {t('result.buttons.showInFinder')}
              </button>
            ) : (
              downloadUrl && (
                <button
                  onClick={() => void handleDownload()}
                  disabled={isDownloading}
                  className="min-h-[44px] flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Download className="h-4 w-4" />
                  {isMp4Output
                    ? t('subtitles.result.downloadMp4', { defaultValue: 'Download MP4' })
                    : t('subtitles.result.downloadZip', { defaultValue: 'Download ZIP' })}
                </button>
              )
            )}

            <button
              onClick={handleReset}
              className="min-h-[44px] px-6 py-3 bg-black/30 border border-white/10 text-white/70 font-medium rounded-lg hover:bg-black/40 hover:text-white transition-colors"
            >
              {t('subtitles.result.generateAnother', { defaultValue: 'Generate another' })}
            </button>
          </div>
        </>
      )}

      {/* Cancelled */}
      {status === 'cancelled' && (
        <div className="flex items-center gap-3">
          <XCircle className="h-5 w-5 text-white/40 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white/70">
              {t('subtitles.result.cancelled', { defaultValue: 'Generation cancelled.' })}
            </p>
            <button
              onClick={handleReset}
              className="text-xs text-purple-400 hover:text-purple-300 underline mt-1"
            >
              {t('subtitles.result.startOver', { defaultValue: 'Start over' })}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {status === 'error' && error && (
        <div className="flex items-start gap-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-300">
              {t('subtitles.errors.failedTitle', { defaultValue: 'Generation failed' })}
            </p>
            <p className="text-xs text-red-400/80 mt-0.5">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
});
